import { type ReactNode, useRef, useState } from 'react';
import { UPLOAD_ACCEPT_ATTRIBUTE } from '@/lib/image-upload';
import { cn } from '@/lib/utils';

/**
 * A file picker that is also a drop target.
 *
 * It is a `<button>` wrapping the preview rather than a styled `<div>` with a `<label>`: the
 * same element then answers a click, a drop and the keyboard, and it carries the accessible
 * name, so there is one control to find instead of two that half-work.
 */
export interface ImageDropZoneProps {
  /** Prefixes the file input's test id — `${id}-file-input`. */
  id: string;
  /** The accessible name of the control, e.g. "Upload a profile picture". */
  label: string;
  disabled?: boolean;
  className?: string;
  onFile: (file: File) => void;
  children: ReactNode;
}

export function ImageDropZone({
  id,
  label,
  disabled = false,
  className,
  onFile,
  children,
}: ImageDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const take = (files: FileList | null) => {
    const file = files?.[0];
    if (file) onFile(file);
  };

  return (
    <>
      <button
        type="button"
        aria-label={label}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          if (disabled) return;
          // Without this the browser navigates to the dropped file instead of firing `drop`.
          event.preventDefault();
          setIsDraggingOver(true);
        }}
        onDragLeave={() => setIsDraggingOver(false)}
        onDrop={(event) => {
          if (disabled) return;
          event.preventDefault();
          setIsDraggingOver(false);
          take(event.dataTransfer.files);
        }}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-1 border-2 border-dashed border-input text-center text-xs text-muted-foreground transition-colors hover:border-primary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-input',
          isDraggingOver && 'border-primary bg-primary/10 text-foreground',
          className,
        )}
      >
        {children}
      </button>

      {/* Off-screen and out of the tab order: the button above is the control. */}
      <input
        ref={inputRef}
        type="file"
        accept={UPLOAD_ACCEPT_ATTRIBUTE}
        tabIndex={-1}
        className="sr-only"
        data-testid={`${id}-file-input`}
        onChange={(event) => {
          take(event.target.files);
          // Picking the same file twice in a row is otherwise not a change event.
          event.target.value = '';
        }}
      />
    </>
  );
}
